import { canDoProjectAction, canWriteSegmentNotes } from '@projektverwaltung/shared';
import type { KfmlWorklistItem, Phase, Segment, SegmentNote } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
/**
 * KFML-Worklist (PV-016) — zwei Tabs unter einem Einstieg:
 *   - „Staffing": DIE Staffing-Ansicht (StaffingNeuPage), nicht kopiert. Eine Änderung dort
 *     wirkt hier automatisch mit.
 *   - „Worklist": ausschließlich Projekte mit gesetztem `kfml_worklist`-Haken. Je Projekt:
 *     Salesforce-Absprung, phasenabhängiger Notizbereich (beschreibbar) und der letzte
 *     Staffing-Kommentar + Eingabe.
 *
 * ABGRENZUNG: Das ist die KFML-WORKLIST — NICHT die KFML-Todoliste (Issue #14,
 * services/kfml-todo.ts, LLM-Extraktion aus Intake-Notizen, kein Feld am Projekt).
 * Deshalb liegen die Texte unter `v2.worklist.*`, nie unter `v2.kfml.*`.
 *
 * Navigations-Einstieg für die Funktionsrolle `kaufm_leitung` anstelle von „Staffing"
 * (sidebar.tsx). Gesperrt ist nichts — /staffing bleibt für alle erreichbar.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { renderMentionBody } from '../../components/mention-input.js';
import { useToast } from '../../components/toast.js';
import { Badge } from '../../components/v2/badge.js';
import { BefundMarker, useBefundUebersicht } from '../../components/v2/befunde/befund-marker.js';
import { Card } from '../../components/v2/card.js';
import { ProjectFilterBar } from '../../components/v2/filters/project-filter-bar.js';
import { FinalBadge } from '../../components/v2/final-kennzeichen.js';
import { HandoverForm, OUTBOUND_FIELDS } from '../../components/v2/handover-form.js';
import type { AssignableUser } from '../../components/v2/inline-assignee.js';
import { IntakeNotesBlock } from '../../components/v2/intake-notes-block.js';
import { KfmlWorklistToggle } from '../../components/v2/kfml-worklist-toggle.js';
import { KlFollowupDateEditor } from '../../components/v2/kl-followup-date-editor.js';
import { LastCommentPreview } from '../../components/v2/last-comment-preview.js';
import { ProjektnotizenBlock } from '../../components/v2/sf-info-card.js';
import { StaffingComments } from '../../components/v2/staffing-comments.js';
import { api } from '../../lib/api.js';
import { formatIsoDate } from '../../lib/format-date.js';
import { lebenszyklusOderNurLesbar, useStaffingPermissions } from '../../lib/permissions.js';
import { useReaderMode } from '../../lib/reader.js';
import { useSession } from '../../lib/session.js';
import { useProjectFilters } from '../../lib/use-project-filters.js';
import { useStickyFilters } from '../../lib/use-sticky-filters.js';
import { StaffingNeuPage } from './staffing-neu.js';

/**
 * Seit #63 liegt die Tab-Wahl am Konto (`view_filters.worklist.tab`) und ist damit
 * geräteübergreifend. Der lokale Schlüssel wird nur noch GELESEN: als Startwert vor
 * dem Laden der Präferenzen und als einmalige Übernahmequelle für Bestandsnutzer.
 */
const TAB_STORAGE_KEY = 'pv:worklist-tab';
type TabKey = 'staffing' | 'worklist';

// Ein Query-Key für beide Konsumenten (Header-Zähler + Tab). React Query dedupliziert →
// eine Anfrage. Derselbe Key, den kfml-worklist-toggle.tsx nach dem Haken invalidiert.
const WORKLIST_QUERY_KEY = ['v2-kfml-worklist'] as const;

// Modul-Konstante statt `data?.items ?? []` inline: ein neues Array-Literal bei jedem
// Render wäre eine neue Referenz, an der `useProjectFilters`/`buildProjectFilterOptions`
// (useMemo-Dependency `opts.items`) bei jedem Render neu rechnen würden (#114).
const EMPTY_ITEMS: KfmlWorklistItem[] = [];

function useWorklistQuery() {
  return useQuery({ queryKey: WORKLIST_QUERY_KEY, queryFn: () => api.getKfmlWorklist() });
}

export function KfmlWorklistPage() {
  // Gewählter Tab überlebt Reload UND Gerätewechsel (Akzeptanzkriterium) → am Konto.
  const [localTab] = useState<TabKey | undefined>(() =>
    window.localStorage.getItem(TAB_STORAGE_KEY) === 'staffing' ? 'staffing' : undefined,
  );
  const [tab, setTab] = useState<TabKey>(() => localTab ?? 'worklist');

  useStickyFilters('worklist', {
    values: { tab },
    // Bestandsnutzer: bisher lokal gemerkte Tab-Wahl einmalig ans Konto ziehen.
    local: { tab: localTab },
    apply: (f) => {
      if (f.tab) setTab(f.tab);
    },
  });

  const { data } = useWorklistQuery();

  return (
    <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-6">
      <Header count={data?.items.length ?? 0} />
      <TabBar active={tab} onChange={setTab} />
      {/* showHeader={false}: die Tab-Leiste trägt die Überschrift bereits (Task 6). */}
      {tab === 'staffing' ? <StaffingNeuPage showHeader={false} /> : <WorklistTab />}
    </div>
  );
}

function Header({ count }: { count: number }) {
  const { t } = useTranslation();
  return (
    <header className="pt-6 animate-fade-up">
      <div
        className="text-[10.5px] uppercase tracking-[0.14em] mb-3"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {t('v2.worklist.eyebrow', 'Kaufm. Leitung')}
      </div>
      <h1
        className="font-medium leading-[1.05] tracking-tight"
        style={{
          fontFamily: 'var(--f-display)',
          color: 'var(--ink)',
          fontSize: 'clamp(22px, 2.4vw, 36px)',
        }}
      >
        {count}{' '}
        <em
          className="not-italic"
          style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
        >
          {t('v2.worklist.headline_emphasis', 'Punkte auf der Worklist')}
        </em>
      </h1>
      <p className="text-sm mt-2 max-w-[640px]" style={{ color: 'var(--ink-3)' }}>
        {t(
          'v2.worklist.subtitle',
          'Manuell vorgemerkte Projekte — Salesforce, Notizen und Staffing-Kommentar an einem Ort. Ein Projekt verlässt die Liste nur durch manuelles Entfernen des Hakens (hier oder in der Übergabe).',
        )}
      </p>
    </header>
  );
}

function TabBar({ active, onChange }: { active: TabKey; onChange: (t: TabKey) => void }) {
  const { t } = useTranslation();
  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'worklist', label: t('v2.worklist.tab_worklist', 'Worklist') },
    { key: 'staffing', label: t('v2.worklist.tab_staffing', 'Staffing') },
  ];
  return (
    <div
      className="flex items-center gap-1 border-b"
      style={{ borderColor: 'var(--line-soft)' }}
      role="tablist"
    >
      {tabs.map((tb) => {
        const isActive = tb.key === active;
        return (
          <button
            key={tb.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tb.key)}
            className="text-sm px-4 py-2 -mb-px transition-colors"
            style={{
              color: isActive ? 'var(--ink)' : 'var(--ink-3)',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${isActive ? 'var(--primary)' : 'transparent'}`,
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {tb.label}
          </button>
        );
      })}
    </div>
  );
}

function WorklistTab() {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useWorklistQuery();
  const phasesQuery = useQuery({ queryKey: ['v2-phases'], queryFn: () => api.listPhases() });
  // Phase je Projekt über die phase_id auflösen (Akzeptanzkriterium #33: Phase auf JEDER
  // Karte, nicht nur bei Handover). Eine Map statt find() je Karte.
  const phaseById = useMemo(() => {
    const map = new Map<string, Phase>();
    for (const ph of phasesQuery.data?.items ?? []) map.set(ph.phase_id, ph);
    return map;
  }, [phasesQuery.data]);

  // Nutzernamen für die Personen-Filter — derselbe Query-Key wie im Staffing-Tab.
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of (usersQuery.data?.items ?? []) as AssignableUser[]) map.set(u.user_id, u.name);
    return map;
  }, [usersQuery.data]);

  const allItems = data?.items ?? EMPTY_ITEMS;
  // #114: dieselben Filter wie im Staffing-Tab, ohne Status (der hängt an Bedarfen).
  // Sortier-Default „Wieder fällig am" aufsteigend = das bisherige feste Verhalten.
  const filters = useProjectFilters({ view: 'worklist_list', items: allItems, nameById });
  const items = filters.filtered;
  // Segment-Notizen: NUR der Segment-Filter wirkt (Entscheidung 2026-09-22) — Notizen
  // haben keinen PL, Manager oder Tag.
  const segment = filters.state.segment;
  const notes = useMemo(
    () => (data?.notes ?? []).filter((n) => !segment || n.segment === segment),
    [data, segment],
  );

  // #114 Fix-Runde: Segment-Chips müssen auch Segmente zeigen, die NUR bei einer Notiz
  // vorkommen (Notizen haben kein eigenes Projekt) — sonst ist so ein Segment nie wählbar,
  // obwohl der Filter selbst (state.segment ist ein freier String) es anstandslos anwenden
  // würde. `filters.options.segment` kennt nur Projekt-Segmente (buildProjectFilterOptions
  // liest ausschließlich `items`); hier um Notiz-Segmente ergänzen, sortiert wie dort.
  const segmentOptions = useMemo(() => {
    const known = new Set(filters.options.segment.map((o) => o.value));
    const extra = new Set<string>();
    for (const n of data?.notes ?? []) {
      if (n.segment && !known.has(n.segment)) extra.add(n.segment);
    }
    if (extra.size === 0) return filters.options.segment;
    return [...filters.options.segment, ...[...extra].map((s) => ({ value: s, label: s }))].sort(
      (a, b) => a.label.localeCompare(b.label),
    );
  }, [filters.options.segment, data?.notes]);

  if (isLoading) {
    return (
      <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
        {t('v2.worklist.loading', 'Lädt…')}
      </p>
    );
  }

  // Fehler VOR dem Leerzustand: bei einem 500er/Netzwerkfehler ist `data` undefined und
  // `items` leer — ohne diesen Zweig läse der User „Keine Projekte auf der Worklist" und
  // damit eine falsche Aussage über den Datenstand plus eine Handlungsanweisung ins Leere.
  if (isError) {
    return (
      <Card>
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {t(
            'v2.worklist.load_failed',
            'Die Worklist konnte nicht geladen werden. Bitte Seite neu laden.',
          )}
        </p>
      </Card>
    );
  }

  // Leerzustand prüft die UNGEFILTERTEN Daten — sonst würde ein aktiver Filter ohne
  // Treffer fälschlich als „gar keine Worklist-Einträge" gemeldet.
  if ((data?.items.length ?? 0) === 0 && (data?.notes.length ?? 0) === 0) {
    return (
      <Card>
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.worklist.empty',
            'Keine Projekte auf der KFML-Worklist. Der Haken wird in der Übergabe-Ansicht gesetzt.',
          )}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <ProjectFilterBar
        filters={{ ...filters, options: { ...filters.options, segment: segmentOptions } }}
        showStatus={false}
      />
      {/* Leerzustand der GEFILTERTEN Sicht. Zwei Fälle, EINE Bedingung:
          (a) Projekte sind durch den Filter weg, Notizen bleiben u. U. sichtbar (das
              bisherige Verhalten, unverändert) — geprüft über `allItems.length > 0`,
              unabhängig davon, ob gerade Notizen zu sehen sind.
          (b) eine reine Notiz-Worklist (nie Projekte, `allItems.length === 0`), bei der
              der Filter zusätzlich ALLE Notizen ausblendet — nur dann greift die zweite
              Alternative (`notes.length === 0 && data?.notes.length > 0`).
          Eine naive Bedingung `items.length === 0 && notes.length === 0 && (...)` würde
          Fall (a) verpassen, sobald Notizen sichtbar bleiben (notes.length > 0) — deshalb
          hier bewusst als ODER mit dem alten Fall statt einer einzigen UND-Kette. */}
      {items.length === 0 &&
        (allItems.length > 0 || (notes.length === 0 && (data?.notes.length ?? 0) > 0)) && (
          <Card>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
                {t('v2.worklist.no_match', 'Keine Treffer für die gewählten Filter.')}
              </p>
              <button
                type="button"
                onClick={filters.reset}
                className="text-sm px-3 py-1.5 rounded-md"
                style={{
                  background: 'var(--surface-2)',
                  color: 'var(--ink)',
                  border: '1px solid var(--line-soft)',
                  cursor: 'pointer',
                }}
              >
                {t('v2.worklist.reset_filters', 'Filter zurücksetzen')}
              </button>
            </div>
          </Card>
        )}
      {items.length > 0 && (
        <div className="space-y-4">
          {items.map((item) => (
            <WorklistCard
              key={item.project.project_id}
              item={item}
              phase={(item.project.phase_id ? phaseById.get(item.project.phase_id) : null) ?? null}
            />
          ))}
        </div>
      )}
      {notes.length > 0 && <WorklistNotesSection notes={notes} />}
    </div>
  );
}

function WorklistCard({ item, phase }: { item: KfmlWorklistItem; phase: Phase | null }) {
  const { t, i18n } = useTranslation();
  const isReader = useReaderMode();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const p = item.project;
  const [expanded, setExpanded] = useState(false);
  const befundEintrag = useBefundUebersicht().get(p.project_id);

  // Rechte am Notizbereich: die BESTEHENDE Matrix, wie in handover.tsx. Bewusst NICHT das
  // Worklist-Recht — wer einen Punkt vormerken darf, darf deshalb noch lange nicht die
  // Notizen ändern (und umgekehrt).
  const scopeShape = {
    project_id: p.project_id,
    segment: (p.segment ?? '') as Segment,
    project_lead_user_id: p.project_lead_user_id ?? null,
    manager_user_id: p.manager_user_id ?? null,
    mtb_user_id: p.mtb_user_id ?? null,
    responsibility: p.responsibility ?? null,
    // #78: hier nur edit/kfml_worklist_manage — der Zuteilungsschutz spielt keine Rolle.
    phase_is_intake: p.phase_is_intake ?? false,
    // #123: `merged` sperrt über die Matrix jede Schreibaktion (auch Notiz und Haken).
    lifecycle_status: lebenszyklusOderNurLesbar(p.lifecycle_status),
  };
  // PV-052 L2 (A11): Das Kennzeichen gibt es auch an in der PV abgeschlossenen Projekten.
  // Dort ist nichts mehr zu bearbeiten (der Server antwortet mit 409) — Notizen und
  // Wiedervorlage werden nur angezeigt. Der Haken selbst bleibt entfernbar.
  // #123: auch ein zusammengeführtes Projekt ist nicht mehr zu bearbeiten (nur `active` ist es).
  const abgeschlossen = scopeShape.lifecycle_status !== 'active';
  const canEditIntake =
    !abgeschlossen &&
    !isReader &&
    user != null &&
    canDoProjectAction(user, scopeShape, 'edit_intake');
  const canEditOutbound =
    !abgeschlossen && !isReader && user != null && canDoProjectAction(user, scopeShape, 'edit');
  // #119: Die Notiz kfm. Leitung bleibt auch an abgeschlossenen Projekten bearbeitbar (die KFML
  // arbeitet nach dem Abschluss kaufmännisch nach) — der Server lässt genau dieses Feld durch.
  const canEditKfmlNotiz =
    !isReader && user != null && canDoProjectAction(user, scopeShape, 'edit');
  // Recht am Haken selbst (entfernen): eigenes `kfml_worklist_manage`, NICHT das Notizrecht —
  // wie in der Übergabe (handover.tsx canManageWorklist).
  const canManageWorklist =
    !isReader && user != null && canDoProjectAction(user, scopeShape, 'kfml_worklist_manage');

  // Rechte am Staffing-Kommentar: die STAFFING-Rechte, bewusst NICHT das Notizrecht.
  // Kommentieren darf, wer Bedarfe verwalten (`staffing_manage`, Bereichsrollen im Scope) oder
  // auflösen darf (`kaufm_leitung`/Admin) — dieselbe Regel wie an den beiden bestehenden
  // Aufruforten (staffing-neu.tsx, project-detail.tsx „Staffing"-Tab), über dieselbe Quelle
  // (useStaffingPermissions) statt nachkodiert. Sonst dürfte derselbe User im Tab „Staffing"
  // kommentieren, im Tab „Worklist" aber nicht.
  // Reader sind ohne Zusatzprüfung außen vor: ohne Funktionsrolle/is_admin liefert die Matrix
  // für beide Rechte false (permissions.ts: `function_roles.length === 0` → Betrachter).
  const { canManage: canManageStaffing, canResolve: canResolveStaffing } =
    useStaffingPermissions(scopeShape);

  // Phasen-Weiche: dieselbe Regel wie in der Übergabe, dort implizit über die Sektion
  // (Eingehend „Neu" → Intake, Ausgehend „Handover" → Handover-Notizen, via kind-Prop).
  // Hier ist die PHASE der direkte Schalter, weil die Worklist Projekte JEDER Phase
  // enthält: Phase „Handover" → Handover-Notizen, sonst → Intake-Notizen.
  // Über den NAMEN, nicht die ID: Phasen sind Admin-konfigurierbar (CRUD), der Name ist der
  // einzige stabile Anker — wie handover.tsx. Ist die Phase unbekannt, greift Intake
  // (gleiche Degradation wie in der Übergabe).
  const isHandover = phase?.name === 'Handover';

  const followupLabel = p.kl_followup_date
    ? formatIsoDate(p.kl_followup_date, i18n.language)
    : null;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: WORKLIST_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['v2-project', p.project_id] });
    void queryClient.invalidateQueries({ queryKey: ['v2-handover-projects'] });
  };

  return (
    <Card padding={false}>
      {/* Dichte Kopfzeile — gesamte Zeile klickt auf/zu (wie HandoverCard / ProjectCard). */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        className="w-full p-4 cursor-pointer select-none"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-xs shrink-0"
            style={{
              color: 'var(--ink-3)',
              transform: expanded ? 'none' : 'rotate(-90deg)',
              transition: 'transform 120ms',
            }}
          >
            ▾
          </span>
          <Link
            to={`/projects/${p.project_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm font-medium hover:underline shrink-0"
            style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)', fontWeight: 600 }}
          >
            {p.project_key}
          </Link>
          <span className="text-sm font-medium truncate" style={{ color: 'var(--ink)' }}>
            {p.customer_name}
          </span>
          {/* Phase auf JEDER Karte (Akzeptanzkriterium #33), Handover bleibt hervorgehoben —
              Tone-Wahl wie in handover.tsx (Handover = warn, sonst primary). */}
          {phase && <Badge tone={isHandover ? 'warn' : 'primary'}>{phase.name}</Badge>}
          {abgeschlossen && item.sf_offen && (
            <span
              className="text-[11px] px-1.5 py-0.5 rounded shrink-0"
              style={{
                background: 'var(--surface-2)',
                color: 'var(--ink-2)',
                border: '1px solid var(--line-soft)',
              }}
            >
              {t('v2.worklist.pv_abgeschlossen', 'in der PV abgeschlossen — SF noch offen')}
            </span>
          )}
          <FinalBadge project={p} />
          <span
            className="text-[11px] shrink-0"
            style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
          >
            · {p.segment}
          </span>
          <span className="text-[11px] truncate hidden md:inline" style={{ color: 'var(--ink-3)' }}>
            {p.name}
          </span>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {befundEintrag && <BefundMarker projectId={p.project_id} eintrag={befundEintrag} />}
            {followupLabel && (
              <span
                className="text-[11px]"
                style={{ color: 'var(--ink-2)', fontFamily: 'var(--f-mono)' }}
                title={t('v2.worklist.followup', 'Wieder fällig am')}
              >
                ⏰ {followupLabel}
              </span>
            )}
            {/* Salesforce-Absprung: FELD IST salesforce_url (nicht sf_url — das gibt es nur im
                DTO der KFML-TODOLISTE, schemas/kfml-todo.ts). Rendering nach dem Vorbild
                KfmlProjectBlock in handover.tsx. */}
            {p.salesforce_url && (
              <a
                href={p.salesforce_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[11px] hover:underline"
                style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)' }}
              >
                ↗ {t('v2.worklist.sf_link', 'In Salesforce öffnen')}
              </a>
            )}
          </div>
        </div>

        {/* PV-015: der letzte Staffing-Kommentar schon in der EINGEKLAPPTEN Zeile — damit man
            beim Überfliegen sieht, wo ein Projekt im Staffing steht, ohne jedes aufzuklappen.
            Aufgeklappt zeigt StaffingComments den Kommentar ohnehin vollständig → nur bei
            `!expanded`. Ohne Kommentar: nichts (kein Platzhalter, ruhigere Liste). */}
        {!expanded && item.last_comment && <LastCommentPreview comment={item.last_comment} />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: 'var(--line-soft)' }}>
          <div className="pt-2" />

          {/* KL-Aktionen zum Projekt: „Wieder fällig am" setzen (schiebt das Projekt bei
              gesetztem Datum in die Staffing-/kl-board-Liste) und der einzige Weg, den Haken
              zu entfernen, wenn das Projekt nicht (mehr) in der Übergabe erscheint (#40).
              Rechte wie in der Übergabe: Datum an staffingPerms.canManage, Haken an
              kfml_worklist_manage. */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span
                className="text-[11px] uppercase tracking-[0.12em]"
                style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
              >
                {t('v2.worklist.followup', 'Wieder fällig am')}
              </span>
              <KlFollowupDateEditor
                projectId={p.project_id}
                value={p.kl_followup_date}
                canEdit={canManageStaffing && !abgeschlossen}
                onSaved={invalidate}
              />
            </div>
            <div className="ml-auto">
              <KfmlWorklistToggle
                projectId={p.project_id}
                value={true}
                canEdit={canManageWorklist}
                variant="remove"
                onSaved={invalidate}
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--line-soft)' }} />

          {/* #119: Salesforce-Projektnotizen Operating read-only, geteilter Block aus
              sf-info-card.tsx (dieselben i18n-Keys wie die Salesforce-Daten-Karte, statt
              einer zweiten Kopie mit eigenem Key). Rein lesend, kein Editier-/Speicherpfad
              hier. Quelle ist `project.salesforce_data.projektnotizen` vom führenden
              Auftrag, kommt bereits über GET /v1/staffing/kfml-worklist mit. */}
          <ProjektnotizenBlock text={p.salesforce_data?.projektnotizen} />

          <div style={{ borderTop: '1px solid var(--line-soft)' }} />

          {/* #119 (Abnahme 28.09.): „Notiz kfm. Leitung" in JEDER Karte bearbeitbar — auch in
              Phase Handover und an abgeschlossenen Projekten; hier arbeitet die KFML. Die
              Handover-Notizen unten zeigen das Feld deshalb nicht noch einmal. Derselbe
              HandoverForm mit nur diesem Feld: er schreibt die übrigen Handover-Felder
              unverändert zurück; Befüllen setzt serverseitig den Worklist-Haken. */}
          <HandoverForm
            kind="outbound"
            title={t('v2.detail.handover_label_kfml_notiz', 'Notiz kfm. Leitung')}
            hint={t(
              'v2.worklist.kfml_notiz_hint',
              'Interne Notiz der kaufmännischen Leitung — in jeder Phase und auch nach dem Abschluss bearbeitbar.',
            )}
            fields={['kfml_notiz']}
            required={new Set()}
            initial={(p.handover_ready_fields ?? {}) as Record<string, string | undefined>}
            projectId={p.project_id}
            canEdit={canEditKfmlNotiz}
            isReader={isReader}
            onSaved={invalidate}
          />

          {/* PV-052 L2 (A11), Fix-Runde 1: Notizen bleiben an abgeschlossenen Projekten SICHTBAR —
              die KL liest hier Restbudget/Folgethemen/Debriefing/Service-Übergabe nach. Nur
              Bearbeitung ist gesperrt (Server antwortet mit 409; einzige Ausnahme ist die Notiz kfm. Leitung oben, #119): `canEditOutbound`/
              `canEditIntake` sind bei `abgeschlossen` bereits `false` (oben) und reichen als
              `canEdit`-Prop an die Blöcke durch — dasselbe Disabled-statt-Verstecken-Muster wie
              `project-detail.tsx` `HandoverTab` (`canEditBase = !isReader &&
              lifecycle_status !== 'completed'`). */}
          {isHandover ? (
            <HandoverForm
              kind="outbound"
              title={t('v2.worklist.notes_handover', 'Handover-Notizen')}
              hint={t('v2.worklist.handover_hint', 'Pflicht beim Phasenwechsel auf Handover.')}
              fields={OUTBOUND_FIELDS.filter((f) => f !== 'kfml_notiz')}
              required={new Set(phase?.required_fields ?? [])}
              initial={(p.handover_ready_fields ?? {}) as Record<string, string | undefined>}
              projectId={p.project_id}
              canEdit={canEditOutbound}
              isReader={isReader}
              onSaved={invalidate}
            />
          ) : (
            // IntakeNotesBlock bringt seine eigene Überschrift („Intake-Notizen") mit —
            // deshalb hier bewusst kein title-Prop und kein eigener v2.worklist-Key.
            <IntakeNotesBlock
              projectId={p.project_id}
              intakeFields={p.intake_handover_fields ?? {}}
              canEdit={canEditIntake}
              isReader={isReader}
              onSaved={invalidate}
            />
          )}

          <div style={{ borderTop: '1px solid var(--line-soft)' }} />

          <div>
            <h3
              className="text-[11px] uppercase tracking-[0.14em] mb-2"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {t('v2.worklist.comments_title', 'Letzter Staffing-Kommentar')}
            </h3>
            {/* limit={1} → der letzte. StaffingComments lädt selbst (nur is_staffing), schreibt
                mit is_staffing: true und bringt den MentionInput mit; item.last_comment aus der
                Liste ist nur die Vorschau-Quelle. Kein zweiter Kommentar-Renderer. */}
            <StaffingComments
              projectId={p.project_id}
              limit={1}
              canComment={canManageStaffing || canResolveStaffing}
              onSaved={invalidate}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * #32 — Notiz-Abschnitt der Worklist. Minimal: Segment-Badge + Notiztext (read-only,
 * @-Erwähnungen gerendert) + „von der Worklist entfernen". Kein „Wieder fällig am".
 */
function WorklistNotesSection({ notes }: { notes: SegmentNote[] }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-medium" style={{ color: 'var(--ink-2)' }}>
        {t('v2.worklist.notes_section_title', 'Notizen ({{count}})', { count: notes.length })}
      </h2>
      {notes.map((n) => (
        <WorklistNoteCard key={n.segment_note_id} note={n} />
      ))}
    </div>
  );
}

function WorklistNoteCard({ note }: { note: SegmentNote }) {
  const { t } = useTranslation();
  const isReader = useReaderMode();
  const { user } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const usersById = useMemo(() => {
    const map = new Map<string, { user_id: string; name: string; email: string }>();
    for (const u of usersQuery.data?.items ?? []) map.set(u.user_id, u);
    return map;
  }, [usersQuery.data]);

  // Recht am Worklist-Flag der Notiz = canWriteSegmentNotes (dasselbe wie Anlegen/Fertig),
  // bewusst NICHT das Projekt-Worklist-Recht.
  const canManage =
    !isReader && user != null && canWriteSegmentNotes(user, note.segment as Segment);

  const remove = useMutation({
    mutationFn: () => api.patchSegmentNote(note.segment_note_id, { kfml_worklist: false }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKLIST_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['v2-segment-notes', note.segment] });
    },
    onError: (err) =>
      toast.error(
        t('v2.worklist.notes_remove_failed', 'Entfernen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Badge tone="primary">{note.segment}</Badge>
          <div className="text-sm whitespace-pre-wrap mt-2" style={{ color: 'var(--ink)' }}>
            {renderMentionBody(note.body, usersById as never)}
          </div>
        </div>
        {canManage && (
          <button
            type="button"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
            className="text-xs px-3 py-1.5 rounded-md shrink-0"
            style={{
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
              opacity: remove.isPending ? 0.5 : 1,
            }}
          >
            {t('v2.worklist.notes_remove', 'von der Worklist entfernen')}
          </button>
        )}
      </div>
    </Card>
  );
}
